import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaBars, FaTimes, FaPhone } from 'react-icons/fa';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Function to scroll to top of page
  const scrollToTop = (e) => {
    // Always allow navigation but add scroll behavior
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, 10); // Very short delay to ensure it works
  };

  // Effect to scroll to top when navigating to home page
  useEffect(() => {
    if (location.pathname === '/') {
      // Scroll to top when we're on the home page
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  return (
    <HeaderContainer scrolled={isScrolled}>
      <div className="container">
        <HeaderContent>
          <LogoContainer>
            <Link to="/" onClick={scrollToTop}>
              <h1>Florida's <span>Top Site Service & Demolition</span></h1>
            </Link>
          </LogoContainer>

          <ContactInfo>
            <FaPhone />
            <div>
              <p>Call us for a free quote</p>
              <a href="tel:+13868722144" style={{display: 'block'}}>(386) 872-2144</a>
              <a href="tel:+15127674123" style={{display: 'block'}}>(512) 767-4123</a>
            </div>
          </ContactInfo>

          <MobileMenuButton onClick={toggleMenu}>
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </MobileMenuButton>

          <NavContainer open={isMenuOpen}>
            <Nav>
              <NavItem active={location.pathname === '/'}>
                <Link to="/">Home</Link>
              </NavItem>
              <NavItem active={location.pathname === '/services'}>
                <Link to="/services">Services</Link>
              </NavItem>
              <NavItem active={location.pathname === '/gallery'}>
                <Link to="/gallery">Gallery</Link>
              </NavItem>
              <NavItem active={location.pathname === '/blog'}>
                <Link to="/blog">Blog</Link>
              </NavItem>
              <NavItem active={location.pathname === '/contact'}>
                <Link to="/contact">Contact</Link>
              </NavItem>
              <GetQuoteButton>
                <Link to="/contact#quote-form" className="btn">Get a Quote</Link>
              </GetQuoteButton>
            </Nav>
          </NavContainer>
        </HeaderContent>
      </div>
    </HeaderContainer>
  );
};

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background-color: ${props => props.scrolled ? 'rgba(0, 0, 0, 0.95)' : 'transparent'};
  transition: background-color 0.3s ease;
  padding: ${props => props.scrolled ? '1rem 0' : '2rem 0'};
  box-shadow: ${props => props.scrolled ? '0 2px 10px rgba(0, 0, 0, 0.5)' : 'none'};

  .container {
    position: relative;
  }

  @media (max-width: 992px) {
    padding: 1.5rem 0;
    background: var(--background-color);
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
`;

const LogoContainer = styled.div`
  a {
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      opacity: 0.8;
      transform: translateY(-2px);
    }
  }

  h1 {
    font-size: 2.4rem;
    color: white;
    margin: 0;
    
    span {
      color: var(--primary-color);
      font-weight: 400;
      font-size: 1.8rem;
      display: block;
    }
  }

  @media (max-width: 768px) {
    h1 {
      font-size: 2rem;
      
      span {
        font-size: 1.6rem;
      }
    }
  }
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  color: white;
  margin-right: 3rem;

  svg {
    background-color: var(--primary-color);
    padding: 1rem;
    border-radius: 50%;
    font-size: 2rem;
    margin-right: 1rem;
  }

  p {
    margin: 0;
    font-size: 1.2rem;
    opacity: 0.8;
  }

  a {
    color: white;
    font-weight: 700;
    font-size: 1.8rem;
  }

  @media (max-width: 992px) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 2.5rem;
  color: white;
  cursor: pointer;
  padding: 0.5rem;
  
  &:hover {
    background: none;
    color: var(--secondary-color);
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 992px) {
    display: block;
  }
`;

const NavContainer = styled.div`
  @media (max-width: 992px) {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    background-color: var(--background-color);
    max-height: ${props => props.open ? '500px' : '0'};
    overflow: hidden;
    transition: max-height 0.3s ease;
    box-shadow: ${props => props.open ? '0 5px 10px rgba(0, 0, 0, 0.5)' : 'none'};
  }
`;

const Nav = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;

  @media (max-width: 992px) {
    flex-direction: column;
    padding: 1rem 0;
  }
`;

const NavItem = styled.li`
  margin: 0 1.5rem;

  a {
    color: white;
    font-weight: 500;
    padding: 0.5rem 0;
    position: relative;
    
    &:hover {
      color: var(--primary-color);
    }

    &::after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: 100%;
      height: 3px;
      background-color: var(--primary-color);
      transform: ${props => props.active ? 'scaleX(1)' : 'scaleX(0)'};
      transform-origin: bottom left;
      transition: transform 0.3s ease;
    }
  }

  &:hover a::after {
    transform: scaleX(1);
  }

  @media (max-width: 992px) {
    margin: 0;
    text-align: center;
    
    a {
      display: block;
      padding: 1.5rem 0;
      font-size: 1.8rem;

      &::after {
        display: none;
      }
    }
  }
`;

const GetQuoteButton = styled.li`
  margin-left: 2rem;

  a {
    padding: 1rem 2rem;
  }

  @media (max-width: 992px) {
    margin: 2rem 0;
    text-align: center;
    
    a {
      display: inline-block;
    }
  }
`;

export default Header; 